import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  RefreshCw, GraduationCap, BookOpen, Search, Eye, Pencil, Library, Layers,
  ChevronRight, ChevronDown,
} from 'lucide-react'
import DashboardSidebar from '../components/DashboardSidebar'
import { lessonApi, courseApi } from '../api/lessonBuilderApi'
import { countCards } from '../lib/sections'
import { flattenChapters, courseGrade, GRADE_LABEL, type CourseDoc } from '../types/CourseDoc'
import type { LessonDoc } from '../types/LessonDoc'

const num = (s?: string) => { const m = String(s || '').match(/\d+/); return m ? parseInt(m[0], 10) : 9999 }

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
    {status === 'published' ? 'Đã xuất bản' : 'Nháp'}
  </span>
)

// Thư viện nội dung CỦA GIÁO VIÊN: chỉ mục khoá học & bài giảng của chính mình.
const ContentLibraryPage: React.FC = () => {
  const navigate = useNavigate()
  const [courses, setCourses] = useState<CourseDoc[]>([])
  const [lessons, setLessons] = useState<LessonDoc[]>([])
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState<'course' | 'ppct'>('course')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all')
  const [gradeFilter, setGradeFilter] = useState('all')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const refresh = () => {
    setLoading(true)
    Promise.all([courseApi.list(), lessonApi.list()])
      .then(([cs, ls]) => { setCourses(cs || []); setLessons(ls || []) })
      .finally(() => setLoading(false))
  }
  useEffect(refresh, [])

  const lessonById = useMemo(() => new Map(lessons.map((l) => [l.id, l] as const)), [lessons])
  const lessonTitle = (l: LessonDoc) => l.meta?.tieuDe || 'Bài giảng'

  const matchSearch = (t: string) => !search.trim() || t.toLowerCase().includes(search.trim().toLowerCase())

  const filteredCourses = useMemo(() => courses.filter((c) =>
    matchSearch(c.title || '') &&
    (statusFilter === 'all' || c.status === statusFilter) &&
    (gradeFilter === 'all' || courseGrade(c) === gradeFilter)
  ), [courses, search, statusFilter, gradeFilter])

  const filteredLessons = useMemo(() => lessons.filter((l) =>
    matchSearch(lessonTitle(l)) &&
    (statusFilter === 'all' || l.status === statusFilter) &&
    (gradeFilter === 'all' || String(l.meta?.lop || '') === gradeFilter)
  ), [lessons, search, statusFilter, gradeFilter])

  const ppctTree = useMemo(() => {
    const byLop = new Map<string, Map<string, LessonDoc[]>>()
    filteredLessons.forEach((l) => {
      const lop = l.meta?.lop || '—'; const chuong = l.meta?.chuong || '—'
      if (!byLop.has(lop)) byLop.set(lop, new Map())
      const chMap = byLop.get(lop)!
      if (!chMap.has(chuong)) chMap.set(chuong, [])
      chMap.get(chuong)!.push(l)
    })
    return Array.from(byLop.entries())
      .sort((a, b) => num(a[0]) - num(b[0]))
      .map(([lop, chMap]) => ({
        lop,
        count: Array.from(chMap.values()).reduce((s, v) => s + v.length, 0),
        chuongs: Array.from(chMap.entries())
          .sort((a, b) => num(a[0]) - num(b[0]))
          .map(([chuong, items]) => ({ chuong, items: items.sort((x, y) => num(x.meta?.bai) - num(y.meta?.bai)) })),
      }))
  }, [filteredLessons])

  const toggle = (id: string) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  const LessonRow: React.FC<{ l: LessonDoc; showPos?: boolean }> = ({ l, showPos }) => (
    <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 rounded-lg">
      <BookOpen className="w-4 h-4 text-blue-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 truncate">{lessonTitle(l)}</div>
        <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
          {showPos && (l.meta?.lop || l.meta?.chuong || l.meta?.bai) && <span>Lớp {l.meta?.lop} · Ch{l.meta?.chuong} · Bài {l.meta?.bai}</span>}
          <span>{countCards(l)} thẻ</span>
        </div>
      </div>
      <StatusBadge status={l.status} />
      <button onClick={() => navigate(`/bai-giang/${l.slug}`)} title="Xem" className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Eye className="w-4 h-4" /></button>
      <button onClick={() => navigate(`/soan-bai/${l.id}`)} title="Sửa" className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg"><Pencil className="w-4 h-4" /></button>
    </div>
  )

  return (
    <div className="flex bg-[#F8FAFC] min-h-screen">
      <DashboardSidebar />
      <main className="flex-1 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2"><Library className="w-7 h-7 text-blue-600" /> Thư viện nội dung</h1>
            <p className="text-sm text-gray-500">Khoá học & bài giảng của bạn — xem theo khoá học hoặc theo Lớp/Chương/Bài</p>
          </div>
          <button onClick={refresh} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg" title="Tải lại"><RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-5 max-w-md">
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-purple-500" />
            <div><div className="text-2xl font-bold text-gray-900">{courses.length}</div><div className="text-xs text-gray-500">Khoá học</div></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-blue-500" />
            <div><div className="text-2xl font-bold text-gray-900">{lessons.length}</div><div className="text-xs text-gray-500">Bài giảng</div></div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden shrink-0">
            <button onClick={() => setView('course')} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold ${view === 'course' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}><GraduationCap className="w-4 h-4" /> Theo khoá học</button>
            <button onClick={() => setView('ppct')} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold ${view === 'ppct' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}><Layers className="w-4 h-4" /> Theo Lớp/Chương/Bài</button>
          </div>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tìm theo tên…" className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white outline-none">
            <option value="all">Mọi trạng thái</option>
            <option value="published">Đã xuất bản</option>
            <option value="draft">Nháp</option>
          </select>
          <select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white outline-none">
            <option value="all">Mọi khối</option>
            <option value="10">Lớp 10</option>
            <option value="11">Lớp 11</option>
            <option value="12">Lớp 12</option>
            <option value="khac">Khác</option>
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 text-gray-500 py-16"><RefreshCw className="w-5 h-5 animate-spin" /> Đang tải…</div>
        ) : view === 'course' ? (
          <div className="space-y-2">
            {filteredCourses.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">Chưa có khoá học khớp bộ lọc.</div>
            ) : filteredCourses.map((c) => {
              const ids = flattenChapters(c.chapters).length ? flattenChapters(c.chapters) : (c.lessonIds || [])
              return (
                <div key={c.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-3">
                    <button onClick={() => toggle(c.id)} className="text-gray-400 hover:text-gray-700">
                      {expanded.has(c.id) ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </button>
                    <GraduationCap className="w-5 h-5 text-purple-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{c.title}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                        <span>{GRADE_LABEL(courseGrade(c))}</span>
                        <span>· {ids.length} bài</span>
                      </div>
                    </div>
                    <StatusBadge status={c.status} />
                    <button onClick={() => navigate(`/khoa-hoc/${c.slug}`)} title="Xem" className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => navigate(`/khoa-hoc/sua/${c.id}`)} title="Sửa" className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg"><Pencil className="w-4 h-4" /></button>
                  </div>
                  {expanded.has(c.id) && (
                    <div className="border-t border-gray-100 bg-gray-50/50 py-2 px-2">
                      {ids.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400">Chưa có bài giảng trong khoá.</div>
                      ) : ids.map((lid) => {
                        const l = lessonById.get(lid)
                        return l ? <LessonRow key={lid} l={l} showPos /> : (
                          <div key={lid} className="px-4 py-2.5 text-sm text-rose-400 italic">Bài đã xoá ({lid.slice(0, 8)}…)</div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <div className="space-y-3">
            {ppctTree.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">Chưa có bài giảng khớp bộ lọc.</div>
            ) : ppctTree.map((lopGroup) => (
              <div key={lopGroup.lop} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-blue-50/60 border-b border-blue-100 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-blue-600" />
                  <span className="font-bold text-blue-800">Lớp {lopGroup.lop}</span>
                  <span className="text-xs text-gray-500">· {lopGroup.count} bài</span>
                </div>
                <div className="p-2 space-y-2">
                  {lopGroup.chuongs.map((ch) => (
                    <div key={ch.chuong}>
                      <div className="px-3 py-1.5 text-xs font-bold text-gray-500 uppercase tracking-wide">Chương {ch.chuong}</div>
                      <div className="space-y-0.5">{ch.items.map((l) => <LessonRow key={l.id} l={l} />)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}

export default ContentLibraryPage
